import { apiErrorMessage } from '@agos/api-client'
import type { BeverageProduct } from '@agos/api-client/model'
import {
  getGetBeverageStockSummaryQueryKey,
  useGetBeverageStockProducts,
  useGetBeverageStockSummary,
  usePostBeverageStockMovement,
} from '@agos/api-client/inventory'
import { T, toDateParam, tt } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  DataTable,
  DateRangePicker,
  FilterBar,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Info, Plus, RefreshCw, Wine, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  BEVERAGE_TABS,
  beverageTotals,
  filterRows,
  formatQty,
  stockBadge,
  tabCounts,
  tabCoverage,
  type BeverageRow,
  type BeverageTab,
} from './beverage-tabs'

/**
 * `/beverage-stock` — what was received, what was poured, and what is left.
 *
 * Legacy's layout: a date range you apply, six category tabs, a search box, the
 * summary table and a *Zaznamenať príjem* modal.
 *
 * **FB-079** is the tab that never worked. Legacy looks for `capovane` — the neuter
 * ending — while both draught products in the data are *Kofola čapovaná* and
 * *Krušovice čapovaný*. Matching the stem finds them, and the tab counts are shown so
 * a tab that holds nothing is visible rather than silently empty.
 *
 * Ported from legacy `pages/beverage-stock.tsx`.
 */

function today(): string {
  return toDateParam(new Date())
}

export function BeverageStockPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<BeverageTab>('all')
  const [search, setSearch] = useState('')
  const [from, setFrom] = useState(today)
  const [to, setTo] = useState(today)
  const [appliedFrom, setAppliedFrom] = useState(today)
  const [appliedTo, setAppliedTo] = useState(today)
  const [receiving, setReceiving] = useState(false)

  // Legacy's own window: the whole of both days, so an evening pour on the last day
  // is inside the range.
  const { data, isLoading } = useGetBeverageStockSummary({
    from: `${appliedFrom}T00:00`,
    to: `${appliedTo}T23:59`,
  })

  const payload = data as { rows?: BeverageRow[]; noStockItems?: string[] } | undefined
  const rows = useMemo<BeverageRow[]>(() => payload?.rows ?? [], [payload])
  const noStockItems = payload?.noStockItems ?? []

  const shown = useMemo(() => filterRows(rows, tab, search), [rows, tab, search])
  const counts = tabCounts(rows)
  const totals = beverageTotals(rows)
  const coverage = tabCoverage(rows)

  const columns: Column<BeverageRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Surovina / Nápoj'),
        sortable: true,
        sortValue: (row) => row.stockName,
        // §9.269 — `truncate` came off. On an `inline` span `overflow` does not apply, so of its
        // three declarations only `white-space: nowrap` took effect — cancelling the `break-words`
        // the phone card asks for and running *Captain Morgan Spiced Gold Rum 35 % 0,4ml* to 449 px
        // inside a 224 px box. It was equally inert in the desktop table, where no name was being
        // shortened by it, so nothing there changes.
        render: (row) => <span className="font-semibold">{row.stockName}</span>,
      },
      {
        key: 'category',
        header: tt('Kategória'),
        sortable: true,
        sortValue: (row) => row.category ?? '',
        render: (row) =>
          row.category ? (
            <StatusBadge label={row.category} color="amber" />
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        key: 'receipt',
        header: tt('Príjem'),
        sortable: true,
        sortValue: (row) => row.receipt ?? 0,
        className: 'text-right font-bold text-emerald-600',
        headerClassName: 'text-right',
        render: (row) =>
          (row.receipt ?? 0) > 0 ? `+${formatQty(row.receipt, row.unit)}` : '-',
      },
      {
        key: 'sales',
        header: tt('Predaj'),
        sortable: true,
        sortValue: (row) => row.sales ?? 0,
        className: 'text-right font-bold text-red-500',
        headerClassName: 'text-right',
        render: (row) => ((row.sales ?? 0) > 0 ? `-${formatQty(row.sales, row.unit)}` : '-'),
      },
      {
        key: 'stockQty',
        header: tt('Zostatok'),
        sortable: true,
        sortValue: (row) => row.stockQty ?? 0,
        className: 'text-right',
        headerClassName: 'text-right',
        render: (row) => {
          const badge = stockBadge(row.stockQty)
          return (
            <div className="flex items-center justify-end gap-2">
              <span
                className={cn(
                  'font-bold text-base tabular-nums',
                  (row.stockQty ?? 0) < 0 && 'text-red-500',
                )}
              >
                {formatQty(row.stockQty, row.unit)}
              </span>
              <StatusBadge label={badge.label} color={badge.color} />
            </div>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Nápojový sklad')}
        description={tt('Sledovanie pohybov nápojov - príjem, predaj a zostatok')}
        icon={<Wine className="w-5 h-5" />}
      />

      <FilterBar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: tt('Hľadať nápoj alebo kategóriu...'),
        }}
        filters={
          <>
            <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
            <button
              type="button"
              onClick={() => {
                setAppliedFrom(from)
                setAppliedTo(to)
              }}
              className={cn('flex items-center gap-2 px-5 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              <RefreshCw className="w-4 h-4" />
              <T>Použiť</T>
            </button>
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => setReceiving(true)}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <T>Zaznamenať príjem</T>
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-beverage-totals>
        <StatCard label={tt('Nápojov')} value={String(totals.rows)} icon={Wine} color="blue" />
        <StatCard label={tt('Predávalo sa')} value={String(totals.salesTotal)} icon={Wine} color="violet" />
        <StatCard
          label={tt('Vypredaných')}
          value={String(totals.empty)}
          icon={Wine}
          color={totals.empty > 0 ? 'amber' : 'emerald'}
        />
        <StatCard
          label={tt('V minuse')}
          value={String(totals.negative)}
          icon={AlertTriangle}
          color={totals.negative > 0 ? 'red' : 'emerald'}
        />
      </div>

      {totals.negative > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-negative-note>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <T>
            Záporný zostatok znamená, že sa vydalo viac, než bolo prijaté - buď chýba príjem, alebo sa
            nápoj účtoval z inej suroviny.
          </T>
        </p>
      )}

      {coverage.unplaced > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{coverage.unplaced}</strong>{' '}
            <T>nápojov nepatrí do žiadnej kategórie v záložkách - nájdete ich pod „Všetky".</T>
          </span>
        </p>
      )}

      {noStockItems.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-no-stock-items>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Tieto nápoje sa predávali, ale nemajú napojenú skladovú surovinu:</T>{' '}
            {noStockItems.join(', ')}
          </span>
        </p>
      )}

      {/* Counts on every tab, so FB-079's empty tab could not have hidden. */}
      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {BEVERAGE_TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setTab(entry.key)}
            aria-pressed={tab === entry.key}
            className={cn(
              'px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px whitespace-nowrap',
              tab === entry.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {entry.label}
            <span className="ml-1.5 text-[10px] font-normal opacity-70">{counts[entry.key]}</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div data-beverage-rows>
          <DataTable<BeverageRow>
            columns={columns}
            data={shown}
            rowKey={(row) => row.stockItemId ?? 0}
            pageSize={25}
            emptyMessage={tt('Žiadne položky v danom rozsahu')}
            emptyIcon={<Wine className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {receiving && (
        <ReceiveDialog
          onClose={() => setReceiving(false)}
          onSaved={() => {
            setReceiving(false)
            void queryClient.invalidateQueries({
              queryKey: getGetBeverageStockSummaryQueryKey(),
            })
          }}
        />
      )}
    </div>
  )
}


function ReceiveDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast()
  const [productId, setProductId] = useState('')
  const [qty, setQty] = useState('')
  const [note, setNote] = useState('')

  const { data, isLoading } = useGetBeverageStockProducts()
  const products = useMemo<BeverageProduct[]>(() => data ?? [], [data])

  const record = usePostBeverageStockMovement({
    mutation: {
      onSuccess: () => {
        toast({ title: tt('Príjem zaznamenaný') })
        onSaved()
      },
      // §9.385 - a delivery the stock guard refused (a negative quantity, an item nobody stocks).
      onError: (caught) =>
        toast({
          title: tt('Chyba'),
          description: apiErrorMessage(caught, tt('Príjem sa nepodarilo zaznamenať.')),
          variant: 'destructive',
        }),
    },
  })

  const amount = Number(qty.trim().replace(',', '.'))
  const ready = productId !== '' && Number.isFinite(amount) && amount > 0

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={tt('Zaznamenať príjem')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-receive-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="font-bold text-sm">
            <T>Zaznamenať príjem</T>
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Nápoj')}
            </span>
            <select
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
              aria-label={tt('Nápoj')}
              disabled={isLoading}
              className={INPUT}
            >
              <option value="">{tt('- Vyberte nápoj -')}</option>
              {products.map((product) => (
                <option key={product.id} value={String(product.id)}>
                  {product.name}
                  {product.categoryName ? ` · ${product.categoryName}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Množstvo')}
            </span>
            <input
              value={qty}
              onChange={(event) => setQty(event.target.value)}
              aria-label={tt('Množstvo príjmu')}
              className={INPUT}
              placeholder="0"
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
              {tt('Poznámka')}
            </span>
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              aria-label={tt('Poznámka k príjmu')}
              className={INPUT}
              placeholder={tt('Napr. faktúra 2026/119')}
            />
          </label>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() =>
              record.mutate({
                data: {
                  productId: Number(productId),
                  qty: amount,
                  note: note.trim() || null,
                },
              })
            }
            disabled={!ready || record.isPending}
            className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50 transition-opacity"
          >
            {record.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Zaznamenať</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'
